import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Stethoscope } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

const LoginSchema = z.object({
  usuario: z.string().min(3, 'Mínimo 3 caracteres').max(50),
  senha: z.string().min(1, 'Senha obrigatória'),
});

type LoginForm = z.infer<typeof LoginSchema>;

export const Route = createFileRoute('/pep-vida/login')({
  beforeLoad: () => {
    if (useAuthStore.getState().user) {
      throw redirect({ to: '/pep-vida/dashboard' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { login, loginPending, loginError, isAuthenticated } = useAuth();
  const [showError, setShowError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'PEP-VIDA | Prontuário Eletrônico do Paciente';
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { usuario: '', senha: '' },
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/pep-vida/dashboard' });
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data: LoginForm) => {
    setShowError(null);
    try {
      await login(data);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Falha no login. Verifique usuário e senha.';
      setShowError(msg);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
            <Stethoscope className="h-7 w-7" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight">PEP-VIDA</h1>
            <p className="text-sm text-muted-foreground">
              Prontuário Eletrônico do Paciente
            </p>
            <p className="text-xs text-muted-foreground">Grupo Vida</p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 rounded-lg border bg-card p-6 shadow-sm"
        >
          <div className="space-y-2">
            <Label htmlFor="usuario">Usuário</Label>
            <Input
              id="usuario"
              autoComplete="username"
              autoFocus
              {...register('usuario')}
              aria-invalid={!!errors.usuario}
            />
            {errors.usuario && (
              <p className="text-xs text-destructive" role="alert">
                {errors.usuario.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="senha">Senha</Label>
            <Input
              id="senha"
              type="password"
              autoComplete="current-password"
              {...register('senha')}
              aria-invalid={!!errors.senha}
            />
            {errors.senha && (
              <p className="text-xs text-destructive" role="alert">
                {errors.senha.message}
              </p>
            )}
          </div>

          {(showError || loginError) && (
            <Alert variant="destructive" data-testid="login-error">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Erro de autenticação</AlertTitle>
              <AlertDescription>
                {showError || loginError?.message}
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={loginPending}
            data-testid="login-submit"
          >
            {loginPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Acesso restrito a profissionais do Grupo Vida.
        </p>
      </div>
    </div>
  );
}
